/**
 * i18n engine — PadelPulse
 *
 * A dependency-free, strictly-typed localization layer built for this app's
 * vanilla-TS / innerHTML rendering model. Views produce HTML strings and are
 * re-rendered on demand by the router, so instead of a framework binding we
 * expose a synchronous `t()` lookup plus a `locale-changed` event that the
 * router listens to in order to re-render the active view instantly (no full
 * page reload).
 *
 * The English dictionary is the single source of truth for the key space:
 * `TranslationKey` is derived from it, so a typo or a key that only exists in
 * one locale is a compile-time error rather than a silent runtime fallback.
 */

import en from "../locales/en/translation.json";
import es from "../locales/es/translation.json";

export type Locale = "en" | "es";

export const DEFAULT_LOCALE: Locale = "en";
export const SUPPORTED_LOCALES: readonly Locale[] = ["en", "es"] as const;

/** Every UI string key, inferred from the English source dictionary. */
export type TranslationKey = keyof typeof en;

/** A complete dictionary must cover every key — enforced on each locale below. */
export type TranslationDictionary = Record<TranslationKey, string>;

// Assigning each locale to `TranslationDictionary` makes a missing key in any
// non-English file a compilation error, keeping translations in lockstep.
const dictionaries: Record<Locale, TranslationDictionary> = { en, es };

const STORAGE_KEY = "locale";
const LOCALE_CHANGED_EVENT = "padelpulse:locale-changed";

let currentLocale: Locale = resolveInitialLocale();

function isSupported(value: string | null | undefined): value is Locale {
  return value != null && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Initial locale precedence: a previously stored user choice, then the browser
 * UI language, then the English default. All lookups are defensive so the app
 * still boots in environments without `localStorage` or `navigator`.
 */
function resolveInitialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isSupported(stored)) return stored;
  } catch {
    // Ignore storage access errors (private mode, disabled cookies, tests).
  }

  const browser = typeof navigator !== "undefined" ? navigator.language.slice(0, 2) : "";
  if (isSupported(browser)) return browser;

  return DEFAULT_LOCALE;
}

/** The active locale. */
export function getLocale(): Locale {
  return currentLocale;
}

/**
 * Switch the active locale. Persists the choice, updates `<html lang>` for
 * accessibility, and notifies subscribers so the current view can re-render.
 * A no-op when the locale is unchanged or unsupported.
 */
export function setLocale(locale: Locale): void {
  if (!isSupported(locale) || locale === currentLocale) return;

  currentLocale = locale;

  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Persistence is best-effort; the in-memory locale is authoritative.
  }

  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }

  window.dispatchEvent(new CustomEvent<Locale>(LOCALE_CHANGED_EVENT, { detail: locale }));
}

/** Values interpolated into `{{placeholder}}` slots within a translation. */
export type TranslationParams = Record<string, string | number>;

/**
 * Translate a key for the active locale. Falls back to English and finally to
 * the raw key, so a partially-translated locale degrades visibly but never
 * throws or renders `undefined`.
 *
 * Supports `{{placeholder}}` interpolation via `params`; an unmatched
 * placeholder is left verbatim so a wiring mistake is visible rather than
 * silently blanked.
 */
export function t(key: TranslationKey, params?: TranslationParams): string {
  const template = dictionaries[currentLocale][key] ?? dictionaries[DEFAULT_LOCALE][key] ?? key;
  if (!params) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

/**
 * Subscribe to locale changes. Returns an unsubscribe function.
 */
export function onLocaleChange(handler: (locale: Locale) => void): () => void {
  const listener = (event: Event) => handler((event as CustomEvent<Locale>).detail);
  window.addEventListener(LOCALE_CHANGED_EVENT, listener);
  return () => window.removeEventListener(LOCALE_CHANGED_EVENT, listener);
}

/**
 * One-time boot hook: reflects the resolved locale onto `<html lang>`. Call
 * once during app startup (see main.ts). Idempotent and safe to omit in tests.
 */
export function initI18n(): void {
  if (typeof document !== "undefined") {
    document.documentElement.lang = currentLocale;
  }
}
